/* istanbul ignore file */

import React, { useEffect } from 'react';
import { Switch, Route, useLocation, useHistory } from '@react-router';
import { JoyKitThemeProvider, createGlobalStyle, Box } from '@withjoy/joykit';

import '@assets/charm.css';
import { TelemetryProvider } from './GuestSite.telemetry';

import { withWindow } from '@shared/utils/withWindow';
import { LazyHotelBookingApp } from './routes/HotelBooking/HotelBooking.lazy';
import { MediaCollection } from './routes/MediaCollection';
import { StandAloneAlohaMenu } from './packages/layout-engine/components/StandAloneAlohaMenu';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { HotelSearchContextParams, HotelSearchSource } from '@shared/constants/accommodations';
import { useQueryParamHelper } from '@shared/core/queryString';
import { EventHandleFetcher } from './components/EventHandleFetcher';
import { LazyRoomBlockEmailRedirect } from './components/RoomBlockEmailRedirect';
import { isHotelEmailClick } from './shared/utils/hotelEmailUtm';
import { setCustomAttribute } from '@shared/utils/logger';
import { UUID_ROUTE_PATTERN } from '@shared/utils/uuid';
import { resolveStayDateQueryParams } from '@shared/utils/dateRangeQueryParams';
import { useMcpPreviewForceSsr } from '@shared/context/mcpPreviewForceSsr';

import { LazyGuestSite } from './routes/GuestSite/GuestSite.lazy';
import { GuestDisplayLocaleProvider } from '@shared/core/localization/context/GuestDisplayLocaleContext';
import { LazyRegistrationGuestWithHead } from './routes/registration/RegistrationGuestWithHead.lazy';
import { LazyEventAccommodations } from '@apps/stays/EventAccommodations/EventAccommodations.lazy';
import { LazyOtaHotelSearch } from './routes/HotelBooking/routes/HotelSearch/OtaHotelSearch.lazy';
import { LazyGuestHotelSearch } from './routes/HotelBooking/routes/HotelSearch/GuestHotelSearch.lazy';
import { LazyHotelSearchHead } from './routes/HotelBooking/routes/HotelSearch/HotelSearchHead.lazy';
import { LazyGuestCarSearch } from './routes/HotelBooking/routes/CarSearch/GuestCarSearch.lazy';
import { LazyGuestCarDetails } from './routes/HotelBooking/routes/CarDetails/GuestCarDetails.lazy';
import { LazyBookYourStay } from './routes/HotelBooking/routes/BookYourStay/BookYourStay.lazy';
import { LazyHotelBookingPartnerSearch } from './routes/HotelBooking/routes/HotelBookingPartnerSearch/HotelBookingPartnerSearch.lazy';
import { LazyAccommodationPlacePartner } from './routes/HotelBooking/routes/AccommodationPlacePartner/AccommodationPlacePartner.lazy';
import { LazyCollaborators } from './routes/Collaborators/Collaborators.lazy';
import { LazyCashFundGuest } from './routes/CashFunds/routes/CashFundGuest/CashFundGuest.lazy';
import { LazyHotelDetails } from './routes/HotelBooking/routes/HotelDetails/HotelDetails.lazy';
import { LazyTicketPass } from './routes/tickets/pass/TicketPass.lazy';
import { LazyWeddingPass } from './routes/wallet/WeddingPass.lazy';
import { LazyPhotoDownload } from './routes/PhotoDownload/PhotoDownload.lazy';
import { LazyMyStay } from './routes/MyStay/MyStay.lazy';
import { LazyInstantRoomBlockGuest } from './routes/InstantRoomBlock/InstantRoomBlockGuest.lazy';
import { INSTANT_ROOM_BLOCK_GUEST_ROUTE_PATH, INSTANT_ROOM_BLOCK_ID_PARAM } from '@shared/instantRoomBlocks/instantRoomBlockGuestRoutes';
import { LazyStaysPdpHead } from '@apps/stays/EventAccommodations/components/StaysPdpHead';

const EventAccommodationsWrapper = ({ eventHandle }: { eventHandle: string }) => {
  const isMobile = useIsMobileScreen();
  const shouldShowAlohaMenu = !isMobile;

  return (
    <>
      <LazyEventAccommodations eventHandle={eventHandle} />
      {shouldShowAlohaMenu && <StandAloneAlohaMenu eventHandle={eventHandle} />}
    </>
  );
};

// MCP preview: replace history with the guest-site root instead of reloading — see MCP_PREVIEW.md#charm-flow-suppression.
const MomentsRoute: React.FC<{ eventHandle: string }> = ({ eventHandle }) => {
  const isMcpPreview = useMcpPreviewForceSsr();
  const history = useHistory();
  const location = useLocation();
  useEffect(() => {
    if (isMcpPreview) {
      history.replace({ pathname: `/${eventHandle}`, search: location.search });
      return;
    }
    withWindow(global => global.location.reload());
  }, [isMcpPreview, eventHandle, history, location.search]);
  return null;
};

const GlobalStyle = createGlobalStyle`
  body {
    font-family: 'Gotham SSm';
  }
`;

export interface GuestProps {}

const HotelSearchRoute = ({ eventHandle }: { eventHandle: string }) => {
  const queryParamHelper = useQueryParamHelper();
  const source = queryParamHelper.getValueString(HotelSearchContextParams.source);
  // Sibling of (not inside) the client-only search bodies, so link-preview tags are in the SSR HTML.
  return (
    <>
      <LazyHotelSearchHead />
      {source === HotelSearchSource.GUEST_MAP ? <LazyGuestHotelSearch eventHandle={eventHandle} /> : <LazyOtaHotelSearch eventHandle={eventHandle} />}
    </>
  );
};

const StaysPdpRoute = ({ joyPlaceId }: { joyPlaceId: string }) => {
  const location = useLocation();
  const queryParamHelper = useQueryParamHelper();
  const searchParams = new URLSearchParams(location.search);
  const eventId = searchParams.get('eventId');
  useEffect(() => {
    setCustomAttribute('joy.stays.eventId', eventId ?? '');
    return () => {
      setCustomAttribute('joy.stays.eventId', '');
    };
  }, [eventId]);
  // Same case-insensitive alias resolution as the search page, so the preview and the page agree on dates.
  const { checkIn, checkOut } = resolveStayDateQueryParams(queryParamHelper);

  const isEmailClick = isHotelEmailClick(location.search);
  // Warm the redirect chunk while EventHandleFetcher is still resolving the handle, so the decision never waits on it.
  useEffect(() => {
    if (eventId && isEmailClick) LazyRoomBlockEmailRedirect.preload();
  }, [eventId, isEmailClick]);

  return (
    <>
      <LazyStaysPdpHead
        joyPlaceId={joyPlaceId}
        eventId={eventId}
        checkIn={checkIn ?? null}
        checkOut={checkOut ?? null}
        instantRoomBlockId={searchParams.get(INSTANT_ROOM_BLOCK_ID_PARAM)}
      />
      <EventHandleFetcher>
        {eventHandle =>
          // Only hotel-email clicks load the redirect chunk; every other visit renders the PDP straight away.
          eventId && isEmailClick ? (
            <LazyRoomBlockEmailRedirect
              eventId={eventId}
              joyPlaceId={joyPlaceId}
              eventHandle={eventHandle}
            >
              <EventAccommodationsWrapper eventHandle={eventHandle} />
            </LazyRoomBlockEmailRedirect>
          ) : (
            <EventAccommodationsWrapper eventHandle={eventHandle} />
          )
        }
      </EventHandleFetcher>
    </>
  );
};

const Guest: React.FC<GuestProps> = () => {
  return (
    <GuestDisplayLocaleProvider>
      <JoyKitThemeProvider>
        <GlobalStyle />
        <Box
          as="main"
          overflowX={['clip', null, 'initial']}
        >
          <Switch>
            {/*
            Standalone stays PDP: /stays/:joyPlaceId?eventId=...
            StaysPdpHead renders OG meta tags during SSR so link-preview crawlers
            (Slack, iMessage, WhatsApp) can display a rich unfurl.
            The PDP body stays client-only via EventHandleFetcher (which uses NoSsr internally).
          */}
            <Route
              path={`/stays/:joyPlaceId(${UUID_ROUTE_PATTERN})`}
              render={({ match }) => {
                return <StaysPdpRoute joyPlaceId={match.params.joyPlaceId} />;
              }}
            />
            <Route
              path="/:eventHandle/moments"
              render={({ match }) => <MomentsRoute eventHandle={match.params.eventHandle} />}
            />
            <Route
              path="/:eventHandle/accommodations/:accommodationId"
              render={({ match }) => {
                return <EventAccommodationsWrapper eventHandle={match.params.eventHandle} />;
              }}
            />
            <Route
              path="/:eventHandle/hotels/:joyPlaceId"
              render={({ match }) => {
                return <EventAccommodationsWrapper eventHandle={match.params.eventHandle} />;
              }}
            />
            <Route
              path="/:eventHandle/accommodation-place/:accommodationPlaceId?"
              render={({ match }) => {
                return <LazyAccommodationPlacePartner accommodationPlaceV2ByIdId={match.params.accommodationPlaceId} />;
              }}
            />
            <Route
              path={'/:eventHandle/hotel'}
              exact
              render={() => <LazyHotelBookingPartnerSearch />}
            />
            <Route
              path="/:eventHandle/car/search"
              render={({ match }) => <LazyGuestCarSearch eventHandle={match.params.eventHandle} />}
            />
            <Route
              path="/:eventHandle/car/details"
              render={({ match }) => <LazyGuestCarDetails eventHandle={match.params.eventHandle} />}
            />
            <Route
              path="/:eventHandle/hotel/search"
              render={({ match }) => <HotelSearchRoute eventHandle={match.params.eventHandle} />}
            />
            <Route
              path="/:eventHandle/hotel/:joyPlaceId?"
              render={({ match }) => (
                <LazyHotelDetails
                  joyPlaceId={match.params.joyPlaceId}
                  eventHandle={match.params.eventHandle}
                />
              )}
            />
            <Route
              path="/:eventHandle/hotelbooking"
              render={({ match }) => {
                const eventHandle = match.params.eventHandle;
                return <LazyHotelBookingApp eventHandle={eventHandle || null} />;
              }}
            />
            <Route
              path="/:eventHandle/book-your-stay"
              render={({ match }) => {
                const eventHandle = match.params.eventHandle;
                return <LazyBookYourStay eventHandle={eventHandle || null} />;
              }}
            />
            <Route
              path="/:eventHandle/albums"
              render={({ match }) => {
                const eventHandle = match.params.eventHandle;
                return (
                  <>
                    <TelemetryProvider context={{ eventName: eventHandle }}>
                      <MediaCollection eventHandle={eventHandle || null} />
                    </TelemetryProvider>
                  </>
                );
              }}
            />
            <Route
              path="/:eventHandle/collaborators"
              render={({ match }) => {
                const eventHandle = match.params.eventHandle;
                return (
                  <TelemetryProvider context={{ eventName: eventHandle }}>
                    <LazyCollaborators eventHandle={eventHandle || null} />
                  </TelemetryProvider>
                );
              }}
            />
            <Route
              path="/:eventHandle/cash-fund/:fundId"
              render={({ match, staticContext }) => {
                const eventHandle = match.params.eventHandle;
                const fundId = match.params.fundId;
                return (
                  <TelemetryProvider context={{ eventName: eventHandle }}>
                    <LazyCashFundGuest
                      eventHandle={eventHandle || null}
                      fundId={fundId || null}
                      staticContext={staticContext}
                    />
                  </TelemetryProvider>
                );
              }}
            />
            <Route
              path="/:eventHandle/tickets/:ticketId/pass"
              render={({ match }) => {
                const { eventHandle, ticketId } = match.params;
                return (
                  <TelemetryProvider context={{ eventName: eventHandle }}>
                    <LazyTicketPass
                      eventHandle={eventHandle}
                      ticketId={ticketId}
                    />
                  </TelemetryProvider>
                );
              }}
            />
            <Route
              path="/:eventHandle/wallet/:passId"
              render={({ match }) => {
                const { eventHandle, passId } = match.params;
                return (
                  <TelemetryProvider context={{ eventName: eventHandle }}>
                    <LazyWeddingPass
                      eventHandle={eventHandle}
                      passId={passId}
                    />
                  </TelemetryProvider>
                );
              }}
            />
            {/*
              Registered BEFORE the page catch-all: "my-stay" is a two-segment URL that would
              otherwise be read as a host-built page slug on the guest site.
            */}
            <Route
              path="/:eventHandle/my-stay"
              render={({ match }) => <LazyMyStay eventHandle={match.params.eventHandle} />}
            />
            {/*
            Landing page for the "photos ready" email. Deliberately outside TelemetryProvider: Segment
            attaches the page URL to every event, and this URL's `file` query is a signed SAS token.
          */}
            <Route
              path="/:eventHandle/photo-download"
              exact
              render={({ match }) => <LazyPhotoDownload eventHandle={match.params.eventHandle} />}
            />
            {/*
            Guest-facing registration form deep link. Registered BEFORE the page catch-all so the
            3-segment URL renders the form rather than being treated as a page slug ("registration").
            The bare /:eventHandle/registration (no formId) is intentionally NOT handled here — it is
            a host-built guest-site page and falls through to the catch-all below.
          */}
            <Route
              path="/:eventHandle/registration/:formId"
              render={({ match }) => {
                const { eventHandle, formId } = match.params;
                if (!eventHandle || !formId) return null;
                return (
                  <TelemetryProvider context={{ eventName: eventHandle }}>
                    <LazyRegistrationGuestWithHead
                      eventHandle={eventHandle}
                      formId={formId}
                    />
                  </TelemetryProvider>
                );
              }}
            />
            {/*
              Registered BEFORE the page catch-all: /:eventHandle/instant-room-block/:id would otherwise be
              read as a page slug on the guest site. The page reads the flag itself: with the flag on, or on for
              existing blocks only, it shows the pages; with the flag off it shows the not-available line, and the checkout still
              shows the card for a payment under way.
            */}
            <Route
              path={INSTANT_ROOM_BLOCK_GUEST_ROUTE_PATH}
              render={({ match }) => {
                const { eventHandle, instantRoomBlockId } = match.params;
                return (
                  <TelemetryProvider context={{ eventName: eventHandle }}>
                    <LazyInstantRoomBlockGuest
                      eventHandle={eventHandle}
                      instantRoomBlockId={instantRoomBlockId}
                    />
                  </TelemetryProvider>
                );
              }}
            />
            {/*
            Unified route handler for:
            1. Custom pages: /:eventHandle/page/:pageSlug (e.g., /myevent/page/custom-content)
            2. Standard pages (or whitelisted custom pages): /:eventHandle/:pageSlug (e.g., /myevent/travel)
            3. Root route: /:eventHandle (renders welcome page or first page by default)

            GuestSite component determines which page to render based on URL structure.
          */}
            <Route
              path={['/:eventHandle/page/:pageSlug', '/:eventHandle/:pageSlug', '/:eventHandle']}
              render={({ match, staticContext }) => {
                const eventHandle = match.params.eventHandle;
                return (
                  <TelemetryProvider context={{ eventName: eventHandle }}>
                    <LazyGuestSite
                      eventHandle={eventHandle || null}
                      staticContext={staticContext}
                    />
                  </TelemetryProvider>
                );
              }}
            />
          </Switch>
        </Box>
      </JoyKitThemeProvider>
    </GuestDisplayLocaleProvider>
  );
};

Guest.displayName = 'Guest';

export default Guest;
